/* Fantastick Google-Bewertungen V1.0.1. Native Elemente, keine Bibliotheken, keine Google-Schlüssel. */
(() => {
  'use strict';
  const RATING_TAG = 'fantastick-google-rating';
  const REVIEWS_TAG = 'fantastick-google-reviews';
  if (customElements.get(RATING_TAG) && customElements.get(REVIEWS_TAG)) return;
  const googleHosts = new Set(['www.google.com', 'google.com', 'maps.google.com', 'www.google.de', 'google.de', 'maps.google.de', 'maps.app.goo.gl', 'g.page', 'goo.gl']);
  const pending = new Map(); // Nur gleichzeitig laufende Anfragen teilen, kein persistenter Cache.
  const germanNumber = new Intl.NumberFormat('de-DE');
  const ratingNumber = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const germanDate = new Intl.DateTimeFormat('de-DE');
  function safeUrl(value, googleOnly = false) {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return '';
      if (googleOnly && !googleHosts.has(url.hostname)) return '';
      return url.href;
    } catch { return ''; }
  }
  function parseDateOnly(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(+date) && date.toISOString().slice(0, 10) === value ? date : null;
  }
  function validateFeed(data) {
    if (!data || data.version !== 1 || data.source !== 'google_places') throw new Error('Datenformat');
    if (!Number.isInteger(data.count) || data.count < 0 || data.count > 10000000) throw new Error('Anzahl');
    if (data.count > 0 && (!Number.isFinite(data.rating) || data.rating < 1 || data.rating > 5)) throw new Error('Sterne');
    const date = new Date(data.fetchedAt);
    if (!Number.isFinite(+date) || +date > Date.now() + 60000 || Date.now() - date > 15 * 60000) throw new Error('Zeitstempel');
    const googleUrl = safeUrl(data.googleUrl, true);
    if (!googleUrl || !Array.isArray(data.attributions) || data.attributions.length > 20) throw new Error('Quelle');
    const attributions = data.attributions.map((entry) => {
      if (!entry || typeof entry.provider !== 'string' || !entry.provider.trim() || entry.provider.length > 200) throw new Error('Quellenangabe');
      const uri = entry.uri ? safeUrl(entry.uri) : '';
      if (entry.uri && !uri) throw new Error('Quellenlink');
      return { provider: entry.provider, uri };
    });
    return { rating: data.count ? data.rating : null, count: data.count, googleUrl, date, attributions };
  }
  function requestRating(url) {
    if (pending.has(url)) return pending.get(url);
    const promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok || Number(response.headers.get('content-length')) > 32768) throw new Error('Antwort');
        const body = await response.text();
        if (body.length > 32768) throw new Error('Antwortgröße');
        return validateFeed(JSON.parse(body));
      } finally { clearTimeout(timeout); }
    })();
    pending.set(url, promise);
    promise.finally(() => pending.delete(url)).catch(() => {});
    return promise;
  }
  class GoogleRating extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this._run = (this._run || 0) + 1;
      const run = this._run;
      try { this.config = JSON.parse(this.querySelector('[data-fgr-config]').textContent); }
      catch { return; } // Die serverseitige, geprüfte Ausgabe bleibt erhalten.
      this.link = this.querySelector('[data-fgr-link]');
      this.applyFallback();
      const checkAge = () => {
        if (this.dataset.state === 'manual' && !this.validManual()) this.linkOnly(this.config.googleUrl);
      };
      document.addEventListener('visibilitychange', checkAge, { signal: this._controller.signal });
      window.addEventListener('pageshow', checkAge, { signal: this._controller.signal });
      if (this.config.source !== 'feed') return;
      const url = safeUrl(this.config.feedUrl);
      if (!url) { this.editorStatus('Automatik: Bitte eine HTTPS-Adresse des eigenen Datenservers eintragen.'); return; }
      requestRating(url).then((data) => {
        if (!this.isConnected || this._run !== run) return;
        if (data.count === 0) {
          this.linkOnly(data.googleUrl); // Eine bestätigte Null überschreibt einen früheren manuellen Stand.
          this.dataset.state = 'empty';
        } else { this.renderRating(data, 'feed'); }
        this.renderAttributions(data.attributions);
        this.editorStatus('Automatik: Daten erfolgreich abgerufen.');
      }).catch(() => {
        if (!this.isConnected || this._run !== run) return;
        this.applyFallback();
        this.editorStatus('Automatik derzeit nicht erreichbar oder Antwort ungültig. Es gilt der erlaubte, aktuelle manuelle Ersatzstand; sonst erscheint nur der Google-Link.');
      });
    }
    disconnectedCallback() { this._controller?.abort(); this._controller = null; this._run++; }
    editorStatus(text) {
      const note = this.nextElementSibling?.querySelector('[data-fgr-editor-status]');
      if (note) note.textContent = text;
    }
    validManual() {
      const manual = this.config.manual || {};
      const date = parseDateOnly(manual.checkedAt);
      return this.config.manualValid === true && safeUrl(this.config.googleUrl, true) && date && +date <= Date.now() && Date.now() - date <= Number(this.config.maxAgeDays) * 86400000 && Number.isFinite(manual.rating) && manual.rating >= 1 && manual.rating <= 5 && Number.isInteger(manual.count) && manual.count > 0 && manual.count <= 10000000;
    }
    applyFallback() {
      if ((this.config.source !== 'feed' || this.config.fallback === true) && this.validManual()) {
        this.renderRating({ ...this.config.manual, googleUrl: this.config.googleUrl, date: parseDateOnly(this.config.manual.checkedAt), attributions: [] }, 'manual');
      } else { this.linkOnly(this.config.googleUrl); }
      this.renderAttributions([]);
    }
    linkOnly(url) {
      url = safeUrl(url, true);
      this.hidden = !url;
      this.dataset.state = 'link';
      this.link.href = url || '#';
      this.link.setAttribute('aria-label', `Google-Bewertungen öffnen${this.config.newTab ? ' (neuer Tab)' : ''}.`);
      this.querySelector('[data-fgr-metrics]').hidden = true;
      this.querySelector('[data-fgr-date]').hidden = true;
      this.querySelector('[data-fgr-empty]').hidden = false;
    }
    renderRating(data, state) {
      const url = safeUrl(data.googleUrl, true);
      if (!url) return this.linkOnly(this.config.googleUrl);
      this.hidden = false;
      this.dataset.state = state;
      this.link.href = url;
      this.querySelector('[data-fgr-metrics]').hidden = false;
      this.querySelector('[data-fgr-empty]').hidden = true;
      this.querySelector('[data-fgr-value]').textContent = ratingNumber.format(data.rating);
      this.querySelectorAll('[data-fgr-stars] .fgr__star').forEach((star, i) => {
        star.style.setProperty('--fgr-fill', `${Math.max(0, Math.min(100, (data.rating - i) * 100))}%`);
      });
      const count = germanNumber.format(data.count);
      const word = data.count === 1 ? 'Bewertung' : 'Bewertungen';
      const formats = { short: `(${count})`, plain: `${count} ${word}`, google: `${count} Google-${word}`, parentheses: `(${count} ${word})` };
      this.querySelector('[data-fgr-count-full]').textContent = formats[this.config.countFormat] || formats.parentheses;
      const short = this.querySelector('[data-fgr-count-short]');
      if (short) short.textContent = `(${count})`;
      const dateText = `${state === 'feed' ? 'Abruf' : 'Stand'} ${germanDate.format(data.date)}`;
      const date = this.querySelector('[data-fgr-date]');
      date.textContent = dateText;
      const showDate = this.config.showDate !== false; // Ältere Abschnitte zeigen das Datum weiterhin an.
      date.hidden = !showDate;
      const dateLabel = showDate ? ` ${dateText}.` : '';
      this.link.setAttribute('aria-label', `${this.config.businessName}: ${ratingNumber.format(data.rating)} von 5 Sternen bei ${count} Google-${word}.${dateLabel} Google-Bewertungen öffnen${this.config.newTab ? ' (neuer Tab)' : ''}.`);
    }
    renderAttributions(entries) {
      const box = this.querySelector('[data-fgr-attributions]');
      box.replaceChildren();
      box.hidden = !entries.length;
      if (!entries.length) return;
      box.append(document.createTextNode('Weitere Datenquellen: '));
      entries.forEach((entry, i) => {
        if (i) box.append(document.createTextNode(' · '));
        const node = document.createElement(entry.uri ? 'a' : 'span');
        node.textContent = entry.provider;
        if (entry.uri) { node.href = entry.uri; node.target = '_blank'; node.rel = 'noopener noreferrer'; }
        box.append(node);
      });
    }
  }
  class GoogleReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-fgr-track]');
      this.cards = Array.from(this.querySelectorAll('[data-fgr-card]'));
      if (!this.track || !this.cards.length) return;
      this.controller = new AbortController();
      this.points = [0];
      this.index = 0;
      this.timer = null;
      this.frame = null;
      this.visible = false;
      this.hovered = false;
      this.manualStop = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoRequested = this.dataset.autoplay === 'true' && this.dataset.designMode !== 'true';
      const on = (target, type, callback, extra = {}) => target?.addEventListener(type, callback, { ...extra, signal: this.controller.signal });
      this.previous = this.querySelector('[data-fgr-prev]');
      this.next = this.querySelector('[data-fgr-next]');
      this.autoButton = this.querySelector('[data-fgr-auto]');
      this.dots = this.querySelector('[data-fgr-dots]');
      this.nav = this.querySelector('[data-fgr-nav]');
      this.status = this.querySelector('[data-fgr-status]');
      on(this.previous, 'click', () => this.move(-1, true));
      on(this.next, 'click', () => this.move(1, true));
      on(this, 'pointerdown', (event) => { if (!event.target.closest('[data-fgr-auto]')) this.stopByUser(); }, { passive: true });
      on(this, 'pointerenter', (event) => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
      on(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      on(this, 'focusin', (event) => { if (!event.target.closest('[data-fgr-auto]')) this.stopByUser(); });
      on(this, 'focusout', () => requestAnimationFrame(() => { if (this.isConnected) this.schedule(); }));
      on(this, 'wheel', () => this.stopByUser(), { passive: true });
      on(this.track, 'keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); this.stopByUser();
        if (event.key === 'Home' || event.key === 'End') this.goTo(event.key === 'Home' ? 0 : this.points.length - 1, true);
        else this.move(event.key === 'ArrowRight' ? 1 : -1, true);
      });
      on(this.track, 'scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updateControls(); });
      }, { passive: true });
      on(this.autoButton, 'click', () => { this.manualStop = !this.manualStop; this.schedule(); });
      on(this.reduced, 'change', () => this.schedule());
      on(document, 'visibilitychange', () => this.schedule());
      on(this, 'click', (event) => {
        const button = event.target.closest('[data-fgr-more]');
        if (button && this.contains(button)) this.openReview(button);
      });
      this.cards.forEach((card) => this.relativeDate(card));
      this.observer = new ResizeObserver(() => this.layout());
      this.observer.observe(this.track);
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .2);
        this.schedule();
      }, { threshold: [0, .2] });
      this.visibilityObserver.observe(this);
      this.classList.add('is-ready');
      this.layout();
      document.fonts?.ready.then(() => { if (this.isConnected && this.controller) this.layout(); });
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); this.visibilityObserver?.disconnect();
      clearTimeout(this.timer); cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
      this.dialog?.remove(); this.dialog = null;
      this.classList.remove('is-ready');
    }
    relativeDate(card) {
      const time = card.querySelector('[data-fgr-review-date]');
      if (!time) return;
      const date = parseDateOnly(time.dateTime);
      if (!date || +date > Date.now()) return;
      const days = Math.floor((Date.now() - date) / 86400000);
      let value = -days, unit = 'day';
      if (days >= 365) { value = -Math.floor(days / 365); unit = 'year'; }
      else if (days >= 30) { value = -Math.floor(days / 30); unit = 'month'; }
      else if (days >= 7) { value = -Math.floor(days / 7); unit = 'week'; }
      time.title = germanDate.format(date);
      time.setAttribute('aria-label', germanDate.format(date));
      time.textContent = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' }).format(value, unit);
    }
    layout() {
      if (!this.controller) return;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const first = this.cards[0].getBoundingClientRect().left;
      this.points = this.cards.map((card) => Math.min(max, Math.max(0, card.getBoundingClientRect().left - first)))
        .filter((value, i, points) => i === 0 || Math.abs(value - points[i - 1]) > 2);
      if (this.dots) {
        this.dots.replaceChildren();
        this.points.forEach((_, index) => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'fgr__dot';
          button.setAttribute('aria-label', `Bewertungsansicht ${index + 1} von ${this.points.length}`);
          button.addEventListener('click', () => { this.stopByUser(); this.goTo(index, true); });
          this.dots.append(button);
        });
      }
      this.cards.forEach((card) => {
        const text = card.querySelector('[data-fgr-review-text]');
        const button = card.querySelector('[data-fgr-more]');
        text.classList.toggle('is-clamped', this.dataset.more === 'true');
        button.hidden = this.dataset.more !== 'true' || text.scrollHeight <= text.clientHeight + 2;
      });
      this.updateControls(); this.schedule();
    }
    updateControls() {
      const left = this.track.scrollLeft;
      this.index = this.points.reduce((best, point, index) => Math.abs(point - left) < Math.abs(this.points[best] - left) ? index : best, 0);
      const canScroll = this.points.length > 1;
      this.nav.hidden = !canScroll;
      if (this.dots) {
        this.dots.hidden = !canScroll;
        [...this.dots.children].forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
      }
      this.previous.disabled = !canScroll || (this.dataset.loop !== 'true' && this.index === 0);
      this.next.disabled = !canScroll || (this.dataset.loop !== 'true' && this.index === this.points.length - 1);
      this.querySelector('[data-fgr-counter]').textContent = `${this.index + 1} / ${this.points.length}`;
    }
    move(direction, user) {
      if (user) this.stopByUser();
      let index = this.index + direction;
      if (this.dataset.loop === 'true') index = (index + this.points.length) % this.points.length;
      else index = Math.max(0, Math.min(this.points.length - 1, index));
      this.goTo(index, user);
    }
    goTo(index, announce = false) {
      this.track.scrollTo({ left: this.points[index], behavior: this.reduced.matches ? 'instant' : 'smooth' });
      if (announce) this.status.textContent = `Bewertungsansicht ${index + 1} von ${this.points.length}`;
    }
    stopByUser() { this.manualStop = true; this.schedule(); }
    schedule() {
      clearTimeout(this.timer); this.timer = null;
      if (!this.controller) return;
      if (this.autoButton) {
        this.autoButton.disabled = this.reduced.matches || this.dataset.designMode === 'true';
        this.autoButton.textContent = this.reduced.matches || this.dataset.designMode === 'true' ? 'Automatik deaktiviert' : this.manualStop ? 'Automatik starten' : 'Automatik pausieren';
      }
      const active = document.activeElement;
      const focusPaused = this.contains(active) && active !== this.autoButton;
      if (!this.autoRequested || this.reduced.matches || this.manualStop || this.hovered || focusPaused || document.hidden || !this.visible || this.points.length < 2) return;
      const seconds = Math.max(3, Math.min(15, Number(this.dataset.interval) || 7));
      this.timer = setTimeout(() => {
        if (this.index === this.points.length - 1 && this.dataset.loop !== 'true') { this.stopByUser(); return; }
        this.move(1, false); this.schedule();
      }, seconds * 1000);
    }
    createDialog() {
      const dialog = document.createElement('dialog'); dialog.className = 'fgr-dialog';
      const id = `${this.closest('[id]')?.id || 'FgrReviews'}-dialog`;
      dialog.id = id; dialog.setAttribute('aria-labelledby', `${id}-title`);
      const header = document.createElement('div'); header.className = 'fgr-dialog__header';
      this.dialogTitle = document.createElement('h3'); this.dialogTitle.id = `${id}-title`;
      const close = document.createElement('button'); close.className = 'fgr-dialog__close'; close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', 'Bewertung schließen');
      close.addEventListener('click', () => dialog.close());
      header.append(this.dialogTitle, close);
      this.dialogBody = document.createElement('div'); this.dialogBody.className = 'fgr-dialog__body';
      dialog.append(header, this.dialogBody); this.append(dialog); this.dialog = dialog;
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        this.opener?.setAttribute('aria-expanded', 'false');
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      });
    }
    openReview(button) {
      const card = button.closest('[data-fgr-card]');
      if (!card) return;
      this.stopByUser();
      if (!this.dialog) this.createDialog();
      this.dialogTitle.textContent = card.getAttribute('aria-label');
      this.dialogBody.replaceChildren();
      ['.fgr-card__head', '.fgr-card__rating', '.fgr-card__text', '.fgr-card__original'].forEach((selector) => {
        const clone = card.querySelector(selector).cloneNode(true);
        clone.removeAttribute('id'); clone.classList.remove('is-clamped');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        this.dialogBody.append(clone);
      });
      this.opener = button; button.setAttribute('aria-controls', this.dialog.id); button.setAttribute('aria-expanded', 'true');
      this.dialog.showModal(); this.dialogBody.scrollTop = 0;
    }
  }
  if (!customElements.get(RATING_TAG)) customElements.define(RATING_TAG, GoogleRating);
  if (!customElements.get(REVIEWS_TAG)) customElements.define(REVIEWS_TAG, GoogleReviews);
})();
